// QuranReader — Hisni Phase 1c / Phase 2.
// Minimal verse reader: ayah list (Arabic + translation), audio per verse,
// mark-for-review. Back navigation returns control to SurahBrowser.
//
// Phase 2 additions (H4):
//   - Long-press (>400 ms) on Arabic text  → TafsirPopup (certified tafsir)
//   - Tap on individual Arabic word span   → WordPopup (root + meaning)
//
// Data sources:
//   - Surah metadata : GET https://api.quran.com/api/v4/chapters/{n}?language=<locale>
//   - Verses          : GET https://api.quran.com/api/v4/verses/by_chapter/{n}?translations=20&...
//   - Audio           : https://everyayah.com/data/Alafasy_128kbps/{s3d}{a3d}.mp3
//   - Progress        : GET /aweni/api/v1/gateway/memoriz/api/surahs/{n}/progress
//   - Mark for review : POST /aweni/api/v1/gateway/memoriz/api/surahs/{n}/start
import type { AweniContext } from '../../types';
import { showTafsirPopup } from './TafsirPopup';
import { showWordPopup } from './WordPopup';

// ─── Data shapes ─────────────────────────────────────────────────────────────

interface SurahMeta {
  id: number;
  name_simple: string;
  name_arabic: string;
  verses_count: number;
  translated_name?: { name: string };
}

interface Verse {
  id: number;
  verse_number: number;
  verse_key: string;
  text_uthmani: string;
  translations?: Array<{ text: string }>;
}

interface AyahProgress {
  ayah_number: number;
  mastery?: number;
  percentage?: number;
}

// ─── Component ───────────────────────────────────────────────────────────────

export class QuranReader {
  private readonly container: HTMLElement;
  private readonly surahNumber: number;
  private readonly ctx: AweniContext;
  private readonly onBack: () => void;

  private surahMeta: SurahMeta | null = null;
  private verses: Verse[] = [];
  private progressMap: Map<number, AyahProgress> = new Map();

  private currentAudio: HTMLAudioElement | null = null;
  private currentAyah: number | null = null;

  constructor(
    container: HTMLElement,
    surahNumber: number,
    ctx: AweniContext,
    onBack: () => void,
  ) {
    this.container = container;
    this.surahNumber = surahNumber;
    this.ctx = ctx;
    this.onBack = onBack;
  }

  async init(): Promise<void> {
    injectStyles();
    this._renderSkeleton();

    const locale = this.ctx.i18n.locale || 'en';
    const token = this.ctx.auth.getToken();

    const [metaResult, versesResult, progressResult] = await Promise.allSettled([
      fetchSurahMeta(this.surahNumber, locale),
      fetchVerses(this.surahNumber, locale),
      token ? fetchAyahProgress(this.surahNumber, token) : Promise.resolve<AyahProgress[]>([]),
    ]);

    if (metaResult.status === 'fulfilled') {
      this.surahMeta = metaResult.value;
    }
    if (versesResult.status === 'fulfilled') {
      this.verses = versesResult.value;
    }
    if (progressResult.status === 'fulfilled') {
      for (const p of progressResult.value) {
        this.progressMap.set(p.ayah_number, p);
      }
    }

    this._render();
  }

  private _renderSkeleton(): void {
    this.container.innerHTML = `
      <div class="hisni-qr">
        <div class="hisni-qr-skeleton">
          ${Array(6).fill('<div class="hisni-qr-card-skeleton"></div>').join('')}
        </div>
      </div>`;
  }

  private _render(): void {
    const t = (k: string, fb: string) => this.ctx.i18n.t(k) || fb;
    const isRTL = this.ctx.i18n.dir === 'rtl';
    const surah = this.surahMeta;

    const surahTitleHtml = surah
      ? `<span class="hisni-qr-title-arabic" dir="rtl" lang="ar">${escHtml(surah.name_arabic)}</span>
         <span class="hisni-qr-title-latin">${escHtml(surah.name_simple)}</span>
         <span class="hisni-qr-verse-count">${surah.verses_count}</span>`
      : `<span class="hisni-qr-title-latin">${escHtml(t('common_loading', 'Loading…'))}</span>`;

    this.container.innerHTML = `
      <div class="hisni-qr" dir="${isRTL ? 'rtl' : 'ltr'}">
        <header class="hisni-qr-header">
          <button
            class="hisni-qr-back"
            data-action="back"
            aria-label="${escHtml(t('hisni_reader_back', 'Back to surahs'))}">
            ←
          </button>
          <div class="hisni-qr-title">${surahTitleHtml}</div>
        </header>

        <div class="hisni-qr-attribution" role="note" aria-label="${escHtml(t('hisni_reader_attribution', 'Quran: King Fahd Complex · Translation: Sahih International · Audio: Al-Afasy'))}">
          ${escHtml(t('hisni_reader_attribution', 'Quran: King Fahd Complex · Translation: Sahih International · Audio: Al-Afasy'))}
        </div>

        <div class="hisni-qr-list" role="list">
          ${this.verses.map(v => this._renderAyahCard(v, t)).join('')}
        </div>
      </div>`;

    // Wire back button
    this.container.querySelector('[data-action="back"]')?.addEventListener('click', () => {
      this._cleanup();
      this.onBack();
    });

    // Wire play buttons
    this.container.querySelectorAll<HTMLButtonElement>('[data-play]').forEach(btn => {
      btn.addEventListener('click', () => {
        const n = parseInt(btn.dataset.play ?? '0', 10);
        if (n > 0) this._playAudio(n);
      });
    });

    // Wire mark-for-review buttons
    this.container.querySelectorAll<HTMLButtonElement>('[data-mark]').forEach(btn => {
      btn.addEventListener('click', () => {
        const n = parseInt(btn.dataset.mark ?? '0', 10);
        if (n > 0) void this._markForReview(n);
      });
    });

    // ── Phase 2: Long-press on Arabic text block → TafsirPopup ──────────────
    this.container.querySelectorAll<HTMLElement>('.hisni-qr-arabic').forEach(el => {
      _wireLongPress(el, () => {
        const vk = el.dataset.verseKey;
        if (vk) void showTafsirPopup(vk, this.ctx);
      });
    });

    // ── Phase 2: Tap individual Arabic word span → WordPopup ────────────────
    this.container.querySelectorAll<HTMLElement>('.ayah-word').forEach(span => {
      const activate = (e: Event) => {
        e.stopPropagation();
        const word = span.dataset.word ?? span.textContent ?? '';
        const vk = span.dataset.verseKey ?? '';
        if (word) void showWordPopup(word, vk, this.ctx);
      };

      span.addEventListener('click', activate);
      // Keyboard activation for screen-reader / keyboard users
      span.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate(e);
        }
      });
    });
  }

  private _renderAyahCard(verse: Verse, t: (k: string, fb: string) => string): string {
    const p = this.progressMap.get(verse.verse_number);
    const mastery =
      p?.mastery != null
        ? p.mastery
        : p?.percentage != null
          ? p.percentage >= 100 ? 3 : p.percentage > 0 ? 1 : 0
          : 0;

    // Mastery dot colour — same token mapping as SurahBrowser progress colours.
    const masteryColor =
      mastery >= 3
        ? 'var(--ln-accent-ceremonial)'
        : mastery >= 1
          ? 'var(--ln-accent)'
          : 'var(--ln-text-secondary)';

    const translation = verse.translations?.[0]?.text ?? '';
    const token = this.ctx.auth.getToken();

    return `
      <div
        class="hisni-qr-card"
        role="listitem"
        id="hisni-qr-ayah-${verse.verse_number}"
        data-ayah="${verse.verse_number}">
        <div class="hisni-qr-card-bar">
          <span class="hisni-qr-verse-key" aria-label="Verse ${escHtml(verse.verse_key)}">${escHtml(verse.verse_key)}</span>
          ${mastery > 0 ? `<span class="hisni-qr-mastery-dot" style="background:${masteryColor}" aria-hidden="true"></span>` : ''}
          <div class="hisni-qr-card-actions">
            <button
              class="hisni-qr-play-btn"
              data-play="${verse.verse_number}"
              aria-label="Play verse ${escHtml(verse.verse_key)}"
              aria-pressed="false">▷</button>
            ${token
              ? `<button
              class="hisni-qr-mark-btn"
              data-mark="${verse.verse_number}"
              aria-label="${escHtml(t('hisni_reader_mark_review', 'Mark for review'))}">☆</button>`
              : ''}
          </div>
        </div>
        <div class="hisni-qr-arabic" dir="rtl" lang="ar" data-verse-key="${escHtml(verse.verse_key)}">${_buildWordSpans(verse.text_uthmani, verse.verse_key)}</div>
        ${translation ? `<div class="hisni-qr-translation">${escHtml(translation)}</div>` : ''}
      </div>`;
  }

  private _playAudio(ayahNumber: number): void {
    const t = (k: string, fb: string) => this.ctx.i18n.t(k) || fb;

    // Toggle: tapping the playing ayah pauses it.
    if (this.currentAyah === ayahNumber && this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
      this.currentAyah = null;
      this._updatePlayUI(ayahNumber, false);
      return;
    }

    // Stop previous playback.
    if (this.currentAudio) {
      this.currentAudio.pause();
      const prev = this.currentAyah;
      this.currentAudio = null;
      this.currentAyah = null;
      if (prev !== null) this._updatePlayUI(prev, false);
    }

    const s = String(this.surahNumber).padStart(3, '0');
    const a = String(ayahNumber).padStart(3, '0');
    const url = `https://everyayah.com/data/Alafasy_128kbps/${s}${a}.mp3`;

    const audio = new Audio(url);
    audio.playbackRate = 1.0;
    this.currentAudio = audio;
    this.currentAyah = ayahNumber;
    this._updatePlayUI(ayahNumber, true);

    const handleEnded = () => {
      if (this.currentAyah !== ayahNumber) return;
      this.currentAudio = null;
      this.currentAyah = null;
      this._updatePlayUI(ayahNumber, false);
      // Auto-advance to next verse (continuous mode).
      const next = ayahNumber + 1;
      const maxAyah = this.surahMeta?.verses_count ?? 0;
      if (next <= maxAyah) this._playAudio(next);
    };

    const handleError = () => {
      if (this.currentAyah !== ayahNumber) return;
      this.currentAudio = null;
      this.currentAyah = null;
      this._updatePlayUI(ayahNumber, false);
      showToast(t('hisni_reader_audio_error', 'Audio unavailable'));
    };

    audio.addEventListener('ended', handleEnded, { once: true });
    audio.addEventListener('error', handleError, { once: true });

    audio.play().catch(() => handleError());
  }

  private _updatePlayUI(ayahNumber: number, playing: boolean): void {
    const card = this.container.querySelector<HTMLElement>(`#hisni-qr-ayah-${ayahNumber}`);
    const btn = this.container.querySelector<HTMLButtonElement>(`[data-play="${ayahNumber}"]`);
    if (card) card.classList.toggle('hisni-qr-card--playing', playing);
    if (btn) {
      btn.textContent = playing ? '⏸' : '▷';
      btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    }
  }

  private async _markForReview(ayahNumber: number): Promise<void> {
    const t = (k: string, fb: string) => this.ctx.i18n.t(k) || fb;
    const token = this.ctx.auth.getToken();
    if (!token) return;

    const card = this.container.querySelector<HTMLElement>(`#hisni-qr-ayah-${ayahNumber}`);

    // SRS flip animation — spec D.
    if (card) {
      card.classList.add('hisni-qr-card--flipping');
      card.addEventListener('animationend', () => {
        card.classList.remove('hisni-qr-card--flipping');
      }, { once: true });
    }

    try {
      const res = await fetch(
        `/aweni/api/v1/gateway/memoriz/api/surahs/${this.surahNumber}/start`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ayah_number: ayahNumber }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showToast(t('hisni_reader_marked', 'Added to review queue'));
    } catch {
      showToast(t('hisni_reader_mark_error', 'Could not save — please try again'));
    }
  }

  private _cleanup(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
      this.currentAyah = null;
    }
  }

  destroy(): void {
    this._cleanup();
    this.container.innerHTML = '';
  }
}

// ─── Fetch helpers ────────────────────────────────────────────────────────────

async function fetchSurahMeta(surahNumber: number, locale: string): Promise<SurahMeta> {
  const res = await fetch(
    `https://api.quran.com/api/v4/chapters/${surahNumber}?language=${encodeURIComponent(locale)}`,
    { cache: 'no-store' },
  );
  if (!res.ok) throw new Error(`Quran API HTTP ${res.status}`);
  const json = (await res.json()) as Record<string, unknown>;
  if (json.chapter && typeof json.chapter === 'object') return json.chapter as SurahMeta;
  throw new Error('Unexpected chapters response shape');
}

async function fetchVerses(surahNumber: number, locale: string): Promise<Verse[]> {
  // Arabic locale: no translation needed — show Uthmani text only.
  const isArabic = locale === 'ar';
  const translationsParam = isArabic ? '' : '&translations=20';
  const res = await fetch(
    `https://api.quran.com/api/v4/verses/by_chapter/${surahNumber}?language=${encodeURIComponent(locale)}&per_page=286&fields=text_uthmani,verse_key${translationsParam}`,
    { cache: 'no-store' },
  );
  if (!res.ok) throw new Error(`Quran API HTTP ${res.status}`);
  const json = (await res.json()) as Record<string, unknown>;
  return Array.isArray(json.verses) ? (json.verses as Verse[]) : [];
}

async function fetchAyahProgress(surahNumber: number, token: string): Promise<AyahProgress[]> {
  const res = await fetch(
    `/aweni/api/v1/gateway/memoriz/api/surahs/${surahNumber}/progress`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error(`Progress API HTTP ${res.status}`);
  const json: unknown = await res.json();
  if (Array.isArray(json)) return json as AyahProgress[];
  const root = json as Record<string, unknown>;
  return Array.isArray(root.data) ? (root.data as AyahProgress[]) : [];
}

// ─── Phase 2 helpers ──────────────────────────────────────────────────────────

/**
 * Build word-level `<span>` elements for an Arabic verse.
 * Each word is wrapped in `.ayah-word` with `data-word` and `data-verse-key`
 * so that `WordPopup` can look up the tapped word.
 */
function _buildWordSpans(text: string, verseKey: string): string {
  return text
    .split(/\s+/)
    .filter(w => w.length > 0)
    .map(w =>
      `<span class="ayah-word" data-word="${escHtml(w)}" data-verse-key="${escHtml(verseKey)}" tabindex="0" role="button" aria-label="${escHtml(w)}">${escHtml(w)}</span>`,
    )
    .join(' ');
}

/**
 * Wire a long-press (>400 ms) handler on an element.
 * Uses `pointerdown` / `pointerup` / `pointercancel` so that it works on
 * both touch and mouse without adding a separate `touchstart` listener.
 */
function _wireLongPress(el: HTMLElement, callback: () => void): void {
  const THRESHOLD = 400; // ms
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancel = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  el.addEventListener('pointerdown', (e: PointerEvent) => {
    // Only handle primary pointer (left click / first touch)
    if (!e.isPrimary) return;
    timer = setTimeout(() => {
      timer = null;
      callback();
    }, THRESHOLD);
  });

  el.addEventListener('pointerup', cancel);
  el.addEventListener('pointercancel', cancel);
  // If the pointer leaves the element before threshold, cancel
  el.addEventListener('pointerleave', cancel);
}

// ─── Styles ───────────────────────────────────────────────────────────────────

function injectStyles(): void {
  if (document.getElementById('hisni-qr-styles')) return;
  const s = document.createElement('style');
  s.id = 'hisni-qr-styles';
  s.textContent = `
    /* ── Flip animation (Motion spec D) ──────────────────────────── */
    @keyframes hisni-qr-flip {
      0%   { transform: rotateY(0deg); }
      50%  { transform: rotateY(30deg); }
      100% { transform: rotateY(0deg); }
    }
    @keyframes hisni-qr-pulse {
      0%, 100% { opacity: 1; }
      50%       { opacity: .45; }
    }
    @media (prefers-reduced-motion: reduce) {
      .hisni-qr-card--flipping  { animation: none !important; }
      .hisni-qr-card-skeleton   { animation: none !important; }
    }

    /* ── Shell ───────────────────────────────────────────────────── */
    .hisni-qr {
      padding: var(--ln-space-2, 8px) 0 var(--ln-space-4, 16px);
    }

    /* ── Header ──────────────────────────────────────────────────── */
    .hisni-qr-header {
      display: flex;
      align-items: center;
      gap: var(--ln-space-3, 12px);
      padding: 0 var(--ln-space-3, 12px) var(--ln-space-3, 12px);
    }

    .hisni-qr-back {
      flex-shrink: 0;
      padding: 6px 12px;
      background: transparent;
      color: var(--ln-accent, #0A0A0A);
      border: 1px solid var(--ln-border, rgba(0,0,0,.12));
      border-radius: var(--ln-radius-full, 9999px);
      font-size: .85rem;
      font-weight: 600;
      cursor: pointer;
      transition: background var(--motion-duration-fast, 120ms) ease;
    }
    .hisni-qr-back:hover   { background: var(--ln-bg-subtle, rgba(0,0,0,.04)); }
    .hisni-qr-back:focus-visible {
      outline: 2px solid var(--ln-accent, #0A0A0A);
      outline-offset: 2px;
    }

    .hisni-qr-title {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 6px;
      min-width: 0;
    }
    .hisni-qr-title-arabic {
      font-size: 1.2rem;
      font-weight: 700;
      font-family: var(--ln-font-arabic, 'Amiri', serif);
      color: var(--ln-text-primary, #111);
      line-height: 1.4;
    }
    .hisni-qr-title-latin {
      font-size: .95rem;
      font-weight: 600;
      color: var(--ln-text-primary, #111);
    }
    .hisni-qr-verse-count {
      font-size: .75rem;
      color: var(--ln-text-secondary);
    }

    /* ── Attribution footer ──────────────────────────────────────── */
    .hisni-qr-attribution {
      font-size: .75rem;
      color: var(--ln-text-secondary);
      padding: 0 var(--ln-space-3, 12px) var(--ln-space-3, 12px);
      line-height: 1.5;
      font-family: var(--ln-font-ui, system-ui, sans-serif);
    }

    /* ── Ayah list ───────────────────────────────────────────────── */
    .hisni-qr-list {
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding: 0 var(--ln-space-3, 12px);
    }

    /* ── Ayah card ───────────────────────────────────────────────── */
    .hisni-qr-card {
      background: var(--ln-bg-card, #fff);
      border: 1px solid var(--ln-border, rgba(0,0,0,.08));
      border-radius: var(--ln-radius-md, 12px);
      padding: var(--ln-space-3, 12px);
      transition:
        border-color var(--motion-duration-fast, 120ms) ease,
        outline var(--motion-duration-fast, 120ms) ease;
    }
    .hisni-qr-card:hover {
      border-color: var(--ln-accent, #0A0A0A);
    }

    /* Playing state — highlight ring */
    .hisni-qr-card--playing {
      outline: 2px solid var(--ln-accent-ceremonial);
      outline-offset: 1px;
    }

    /* Flip animation state */
    .hisni-qr-card--flipping {
      animation: hisni-qr-flip var(--motion-duration-slow, 350ms) ease;
      transform-style: preserve-3d;
    }

    /* ── Card bar (verse key + dot + actions) ────────────────────── */
    .hisni-qr-card-bar {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: var(--ln-space-2, 8px);
    }
    .hisni-qr-verse-key {
      font-size: .72rem;
      font-weight: 700;
      color: var(--ln-text-secondary);
      flex-shrink: 0;
    }
    .hisni-qr-mastery-dot {
      width: 7px;
      height: 7px;
      border-radius: var(--ln-radius-full, 9999px);
      flex-shrink: 0;
    }
    .hisni-qr-card-actions {
      margin-inline-start: auto;
      display: flex;
      gap: 6px;
    }

    /* ── Play / Mark buttons ─────────────────────────────────────── */
    .hisni-qr-play-btn,
    .hisni-qr-mark-btn {
      padding: 4px 10px;
      background: transparent;
      border: 1px solid var(--ln-border, rgba(0,0,0,.12));
      border-radius: var(--ln-radius-sm, 6px);
      cursor: pointer;
      font-size: .82rem;
      color: var(--ln-text-secondary);
      transition:
        background var(--motion-duration-fast, 120ms) ease,
        color var(--motion-duration-fast, 120ms) ease;
    }
    .hisni-qr-play-btn:hover,
    .hisni-qr-mark-btn:hover {
      background: var(--ln-bg-subtle, rgba(0,0,0,.04));
      color: var(--ln-accent, #0A0A0A);
    }
    .hisni-qr-play-btn:focus-visible,
    .hisni-qr-mark-btn:focus-visible {
      outline: 2px solid var(--ln-accent, #0A0A0A);
      outline-offset: 2px;
    }
    .hisni-qr-play-btn[aria-pressed="true"] {
      background: var(--ln-accent, #0A0A0A);
      color: var(--ln-accent-text, #fff);
      border-color: transparent;
    }

    /* ── Arabic verse text ───────────────────────────────────────── */
    .hisni-qr-arabic {
      font-family: var(--ln-font-arabic, 'Amiri', serif);
      font-size: clamp(1.1rem, 3vw, 1.5rem);
      line-height: 2;
      color: var(--ln-text-primary, #111);
      text-align: right;
      margin-bottom: var(--ln-space-2, 8px);
      /* Long-press on the whole block opens TafsirPopup */
      user-select: none;
      -webkit-user-select: none;
    }

    /* ── Individual word spans (Phase 2) ─────────────────────────── */
    .ayah-word {
      display: inline;
      cursor: pointer;
      border-radius: 3px;
      padding: 0 1px;
      transition: background var(--motion-duration-fast, 120ms) ease;
    }
    .ayah-word:hover,
    .ayah-word:focus-visible {
      background: var(--ln-accent, #0A0A0A);
      color: var(--ln-accent-text, #fff);
      outline: none;
    }
    .dark .ayah-word:hover,
    .dark .ayah-word:focus-visible {
      background: var(--ln-accent, #C8A96A);
      color: #111;
    }

    /* ── Translation text ────────────────────────────────────────── */
    .hisni-qr-translation {
      font-family: var(--ln-font-ui, system-ui, sans-serif);
      font-size: .9rem;
      color: var(--ln-text-secondary);
      line-height: 1.6;
      direction: ltr;
      text-align: start;
    }

    /* ── Skeleton loading ────────────────────────────────────────── */
    .hisni-qr-skeleton {
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding: 0 var(--ln-space-3, 12px);
    }
    .hisni-qr-card-skeleton {
      height: 120px;
      border-radius: var(--ln-radius-md, 12px);
      background: var(--ln-bg-subtle, rgba(0,0,0,.06));
      animation: hisni-qr-pulse 1.4s ease-in-out infinite;
    }

    /* ── Dark mode ───────────────────────────────────────────────── */
    .dark .hisni-qr-card {
      background: rgba(255,255,255,.04);
      border-color: rgba(255,255,255,.10);
    }
    .dark .hisni-qr-card:hover { border-color: var(--ln-accent, #C8A96A); }
    .dark .hisni-qr-card-skeleton { background: rgba(255,255,255,.08); }
    .dark .hisni-qr-back { color: var(--ln-accent, #C8A96A); }
    .dark .hisni-qr-play-btn:hover,
    .dark .hisni-qr-mark-btn:hover {
      background: rgba(255,255,255,.08);
      color: var(--ln-accent, #C8A96A);
    }
    .dark .hisni-qr-play-btn[aria-pressed="true"] {
      background: var(--ln-accent, #C8A96A);
      color: var(--ln-accent-text, #111);
    }
  `;
  document.head.appendChild(s);
}

// ─── Shared UI helpers ────────────────────────────────────────────────────────

function showToast(message: string): void {
  const existing = document.getElementById('hisni-qr-toast');
  if (existing) existing.remove();
  const toast = document.createElement('div');
  toast.id = 'hisni-qr-toast';
  toast.setAttribute('role', 'status');
  toast.style.cssText = `
    position:fixed;bottom:100px;left:50%;transform:translateX(-50%);
    padding:10px 20px;border-radius:var(--ln-radius-full,9999px);font-size:.85rem;font-weight:500;
    background:var(--ln-text-primary,#111);color:var(--ln-bg-primary,#fff);
    box-shadow:0 4px 12px rgba(0,0,0,.18);z-index:9999;white-space:nowrap;
  `;
  toast.textContent = message;
  document.body.appendChild(toast);
  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transition = 'opacity 300ms ease';
    setTimeout(() => toast.remove(), 320);
  }, 2500);
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
